import {
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import { useReducedMotion } from "motion/react";

import { Box, useToken } from "@hightouch/htui";

import { Drawer } from "src/components/drawer";
import { Breadcrumbs } from "src/components/layout/breadcrumbs";
import {
  NavDrillsInPlaceContext,
  useNavPlace,
} from "src/components/layout/nav/amp-nav";
import {
  type MiniNavEvent,
  type MiniNavOverlay,
  miniNavReducer,
  placeTrail,
  usePeekIntent,
} from "src/components/layout/nav/mini-nav/mini-nav-state";
import { SidebarResizeHandle } from "src/components/layout/nav/mini-nav/sidebar-resize-handle";
import { setNavFolding } from "src/components/layout/nav/nav-fold";
import { Nav } from "src/components/layout/nav/nav";
import {
  focusNavTarget,
  focusQuietly,
  isFocusLost,
  NAV_LIST_SELECTOR,
  NAV_TOGGLE_SELECTOR,
} from "src/components/layout/nav/nav-focus";
import { navMotion } from "src/components/layout/nav/nav-motion";
import { NavRowHeightContext } from "src/components/layout/nav/nav-item-styles";
import { isTypingTarget } from "src/components/preview-canvas/is-typing-target";
import {
  NavWidthContext,
  type NavWidthContextType,
  useNavWidth,
} from "src/contexts/nav-width-context";
import * as analytics from "src/lib/analytics";
import { useMountCount } from "src/hooks/use-mount-count";
import { useLocation } from "src/router";
import { hasPlatformModifier } from "src/utils/platform";
import {
  type CrumbTrailProps,
  CrumbBarEndSlotContext,
  GeneratedCrumbBarContext,
  isSidebarShortcut,
  useCrumbBarEndSlots,
  MINI_NAV_TOGGLE_ROW_SELECTOR,
  MINI_NAV_TRIGGER_SELECTOR,
  MiniNavContext,
  type MiniNavContextValue,
  MiniNavTriggerContext,
  type MiniNavTriggerValue,
  MINI_NAV_SHEET_ATTRIBUTE,
  MiniNavPeekContext,
  MiniNavSidebarContext,
  MiniNavTrigger,
} from "src/components/layout/nav/mini-nav/mini-nav-context";

/** The page's inset from the window's right edge, and from its left while the sidebar is collapsed, in place of the gutter the sidebar gave it. */
export const MINI_NAV_PAGE_INSET = 1.5;

/** The peeked sidebar's inset from the window's left and bottom edges, the page card's own, so the two read as one family of surfaces. */
const PEEK_INSET = 2;

/** Wider than the page's inset, so a pointer heading left peeks the sidebar before it reaches the window's edge, where the browser opens its own side panel. */
const PEEK_GUTTER_WIDTH = 4;

const DOUBLE_CLICK_WINDOW_MS = 500;

/** The double-click's second press on the sidebar's edge lands on the page that slid under it, so it selects no text. */
const ignoreDoubleClickOnPage = () => {
  const onMouseDown = (event: MouseEvent) => {
    if (event.detail > 1) event.preventDefault();
  };
  document.addEventListener("mousedown", onMouseDown, true);
  setTimeout(
    () => document.removeEventListener("mousedown", onMouseDown, true),
    DOUBLE_CLICK_WINDOW_MS,
  );
};

/** The collapsed sidebar's toggle on show, passing over one that a suspended page keeps hidden. */
const getToggle = () =>
  Array.from(document.querySelectorAll(MINI_NAV_TRIGGER_SELECTOR)).find(
    (toggle) => toggle.getClientRects().length > 0,
  ) ?? null;

/** The peeked sidebar opens below the row its toggle sits in, so the toggle stays in reach to dock it. */
const measurePeekTop = (peekInset: number) => {
  const toggle = getToggle();
  const row = toggle?.closest(MINI_NAV_TOGGLE_ROW_SELECTOR) ?? toggle;
  return Math.max(
    peekInset,
    Math.round(row?.getBoundingClientRect().bottom ?? 0),
  );
};

/** Docking from a peek keeps the panel's chrome until the layout commits, as it glides from its inset to the corner. */
type Fold = "collapsing" | "expanding" | "dockingPeek";

/** A spacing token in px, for the slides' arithmetic; htui's spacing tokens are px. */
const useSpacePx = (space: number) =>
  Number.parseFloat(useToken("space", String(space)));

/** Owns the mini-nav: the sidebar docked or collapsed, its peek from the toggle or the gutter, the phone's sheet, and the fold. */
export const MiniNavProvider = ({
  isEligible,
  hasCrumbBars,
  isActive,
  pageRef,
  children,
}: {
  /** The nav is the mini-nav and the page shows a nav, so this renders the sidebar. */
  isEligible: boolean;
  /** The page doesn't hide the sidebar's toggle, so its card opens with a crumb bar to hold it. */
  hasCrumbBars: boolean;
  /** The sidebar is collapsed and the page doesn't hide its toggle, so a crumb bar shows it, and it and the gutter peek. */
  isActive: boolean;
  /** The page beside the sidebar, which slides with it when it docks or collapses. */
  pageRef: RefObject<HTMLElement>;
  children: ReactNode;
}) => {
  const nav = useNavWidth();
  const { pathname } = useLocation();
  const isReducedMotion = useReducedMotion();
  const pageInset = useSpacePx(MINI_NAV_PAGE_INSET);
  const peekInset = useSpacePx(PEEK_INSET);
  // Keyed by the page it opened on, so a navigation closes it without an effect.
  const [opened, setOpened] = useState({
    overlay: "closed" as MiniNavOverlay,
    pathname,
  });
  // A sheet only where the sidebar can't dock, and a peek only where it can, both only while it's collapsed.
  // Docking or resizing the window drops one for good, so it never comes back open on its own.
  const isOverlayPossible =
    !nav.isNavOpen &&
    (opened.overlay === "sheet" ? !nav.canNavOpen : nav.canNavOpen);
  if (opened.overlay !== "closed" && !isOverlayPossible) {
    setOpened({ overlay: "closed", pathname });
  }
  const overlay =
    isEligible && opened.pathname === pathname ? opened.overlay : "closed";
  const [fold, setFold] = useState<Fold | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const [peekTop, setPeekTop] = useState(peekInset);
  const isHeld = useRef(false);
  const isSheetFromKeyboard = useRef(false);
  const linkFollowedAt = useRef(0);
  const foldSlides = useRef<Animation[]>([]);
  /** Whether focus moves to the toggle left showing once a fold commits: always, only if it fell to the body, or not. */
  const focusAfterFold = useRef<"always" | "ifLost" | null>(null);
  // Outside the context's value, so a bar mounting re-renders only the provider.
  const { count: crumbBarCount, register: registerCrumbBar } = useMountCount();
  const { count: loadingStateCount, register: registerLoadingState } =
    useMountCount();
  const crumbBarEndSlot = useCrumbBarEndSlots();

  const dispatch = useMemo(
    () => (event: MiniNavEvent) =>
      setOpened((previous) => ({
        overlay: miniNavReducer(
          previous.pathname === pathname ? previous.overlay : "closed",
          event,
        ),
        pathname,
      })),
    [pathname],
  );
  const dispatchPeek = useCallback(
    (event: MiniNavEvent) => {
      if (event === "peek") setPeekTop(measurePeekTop(peekInset));
      dispatch(event);
    },
    [dispatch, peekInset],
  );
  const peek = usePeekIntent({
    state: overlay,
    dispatch: dispatchPeek,
    gutterRef,
    getToggle,
    panelRef: frameRef,
    isHeld,
  });

  const commit = (isOpen: boolean) => {
    setFold(null);
    nav.setIsNavOpen(isOpen);
  };

  // Slides the sidebar and the page together, the page at its wider collapsed layout so no gap opens beside it.
  // Docking commits the layout on the last frame; collapsing commits it first and slides from the old spot.
  // The page's width eases with its slide, so its right edge holds still and centered or right-aligned content moves smoothly.
  const foldTo = (
    isOpen: boolean,
    source: "toggle" | "shortcut" | "resize" | "edge",
    isKeyboard: boolean,
  ) => {
    if (fold || isOpen === nav.isNavOpen) return;
    analytics.track(isOpen ? "nav_docked" : "nav_undocked", { source });
    if (source === "edge") ignoreDoubleClickOnPage();
    const frame = frameRef.current;
    // From the keyboard, focus in the sidebar as it collapses, or on the toggle that docks it, moves to the toggle left showing.
    // A pointer's fold, or a shortcut pressed with focus elsewhere, moves no focus, so no toggle's tooltip opens unasked.
    focusAfterFold.current = !isKeyboard
      ? null
      : isOpen
        ? document.activeElement?.matches(MINI_NAV_TRIGGER_SELECTOR)
          ? "ifLost"
          : null
        : frame?.contains(document.activeElement)
          ? "always"
          : null;
    const page = pageRef.current;
    // A resize has already moved the sidebar and page into their collapsed layout.
    if (source === "resize" || isReducedMotion || !frame || !page) {
      commit(isOpen);
      return;
    }
    // The page's content moves by the sidebar's width, less its inset from the window's edge when collapsed.
    const shift = frame.offsetWidth - pageInset;
    // A peeked panel glides from its inset to the corner; a hidden sidebar comes in from just off the window's edge.
    const isFromPeek = overlay === "peek";
    const frameFrom = isFromPeek
      ? `translate(${peekInset}px, ${peekTop}px)`
      : "translateX(-100%)";
    if (isOpen) {
      setFold(isFromPeek ? "dockingPeek" : "expanding");
    } else {
      // The collapsed layout is in the DOM before paint, so the page slides from where the docked sidebar left it.
      flushSync(() => {
        setFold("collapsing");
        nav.setIsNavOpen(false);
      });
    }
    const pageWidth = page.getBoundingClientRect().width;
    const collapsedWidth = `${pageWidth}px`;
    const dockedWidth = `${pageWidth - shift}px`;
    const moves: [HTMLElement, Keyframe, Keyframe][] = isOpen
      ? [
          [
            frame,
            // A peeked panel also grows from its inset height to the window's and squares its corners as it reaches the corner.
            isFromPeek
              ? {
                  transform: frameFrom,
                  height: `${frame.offsetHeight}px`,
                  borderRadius: getComputedStyle(frame).borderTopLeftRadius,
                }
              : { transform: frameFrom },
            isFromPeek
              ? {
                  transform: "translate(0, 0)",
                  height: `${window.innerHeight}px`,
                  borderRadius: "0px",
                }
              : { transform: "translate(0, 0)" },
          ],
          [
            page,
            { transform: "translateX(0)", maxWidth: collapsedWidth },
            { transform: `translateX(${shift}px)`, maxWidth: dockedWidth },
          ],
        ]
      : [
          // Ends a peek inset further out, so the hidden panel takes over offscreen once it floats again.
          [
            frame,
            { transform: "translateX(0)" },
            { transform: `translateX(calc(-100% - ${peekInset}px))` },
          ],
          [
            page,
            { transform: `translateX(${shift}px)`, maxWidth: dockedWidth },
            { transform: "translateX(0)", maxWidth: collapsedWidth },
          ],
        ];
    // Content wider than the page's easing width can overflow it mid-fold; no scrollbar flashes for it.
    document.documentElement.style.setProperty("overflow-x", "clip");
    setNavFolding(true);
    const slides = moves.map(([element, from, to]) =>
      element.animate(
        // A property the compositor can't animate keeps the sidebar's slide in the page's frames, so a busy frame
        // holds both still together instead of letting the sidebar run ahead and open a gap beside the page.
        element === frame
          ? [
              { ...from, visibility: "visible" },
              { ...to, visibility: "visible" },
            ]
          : [from, to],
        {
          duration:
            (isOpen ? navMotion.unfoldDuration : navMotion.foldDuration) * 1000,
          easing: `cubic-bezier(${navMotion.foldEase.join(", ")})`,
          fill: "forwards",
        },
      ),
    );
    foldSlides.current = slides;
    const end = () => (isOpen ? commit(true) : setFold(null));
    Promise.all(slides.map(({ finished }) => finished)).then(end, () => {
      // The slides come off once their fold is done; any other cancel still owes the fold its end.
      if (foldSlides.current === slides) end();
    });
  };

  // Once a fold ends, the page already sits where its slide left it, so the slide comes off before paint.
  useLayoutEffect(() => {
    if (fold) return;
    for (const slide of foldSlides.current) slide.cancel();
    foldSlides.current = [];
    document.documentElement.style.removeProperty("overflow-x");
    setNavFolding(false);
    // A drag that collapsed the sidebar left its width on the frame; the saved width comes back for the next dock.
    frameRef.current?.style.removeProperty("width");
    const move = focusAfterFold.current;
    focusAfterFold.current = null;
    if (move === "always" || (move === "ifLost" && isFocusLost())) {
      focusNavTarget(
        nav.isNavOpen ? NAV_TOGGLE_SELECTOR : MINI_NAV_TRIGGER_SELECTOR,
      );
    }
  }, [fold, nav.isNavOpen]);

  const toggleDocked = (source: "toggle" | "shortcut", isKeyboard: boolean) => {
    if (nav.canNavOpen) {
      foldTo(!nav.isNavOpen, source, isKeyboard);
      return;
    }
    isSheetFromKeyboard.current = isKeyboard;
    dispatch(overlay === "sheet" ? "close" : "sheet");
  };

  // A peek closing under the keyboard's focus hands it to the toggle, before the hidden sidebar drops it to the body.
  const overlayBefore = useRef(overlay);
  useLayoutEffect(() => {
    const wasPeek = overlayBefore.current === "peek";
    overlayBefore.current = overlay;
    if (!wasPeek || overlay !== "closed" || nav.isNavOpen) return;
    const toggle = getToggle();
    if (
      toggle instanceof HTMLElement &&
      frameRef.current?.contains(document.activeElement) &&
      document.activeElement?.matches(":focus-visible")
    ) {
      focusQuietly(toggle, { preventScroll: true });
    }
  }, [overlay, nav.isNavOpen]);

  const previousOverlay = useRef(overlay);
  useEffect(() => {
    if (previousOverlay.current === "closed" && overlay === "peek") {
      analytics.track("nav_peek_opened");
    }
    previousOverlay.current = overlay;
    if (overlay !== "peek") isHeld.current = false;
  }, [overlay]);

  // The toggle's row moves as the page scrolls or loads under a peek, and the peeked sidebar keeps right below it.
  useEffect(() => {
    if (overlay !== "peek") return;
    let frame = 0;
    const remeasure = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setPeekTop(measurePeekTop(peekInset));
      });
    };
    // Capturing, so a pane's scroll counts too, though it doesn't bubble.
    window.addEventListener("scroll", remeasure, {
      capture: true,
      passive: true,
    });
    document.addEventListener("pointermove", remeasure, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", remeasure, { capture: true });
      document.removeEventListener("pointermove", remeasure);
    };
  }, [overlay, peekInset]);

  // The latest handlers behind stable ones, so a peek or a fold neither resubscribes the window's keys nor re-renders
  // the pages that show the trigger.
  const handleKeyDown = (event: KeyboardEvent) => {
    // Any input after a followed link is the viewer's own, so no trigger mounting later takes focus from it.
    linkFollowedAt.current = 0;
    // `[` is the sidebar's shortcut; Cmd+/ is the nav's older one, which this takes over to fold the page along.
    if (
      isSidebarShortcut(event) ||
      (hasPlatformModifier(event) &&
        event.key === "/" &&
        !isTypingTarget(document.activeElement))
    ) {
      event.preventDefault();
      toggleDocked("shortcut", true);
    } else if (
      event.key === "Escape" &&
      !event.defaultPrevented &&
      overlay === "peek"
    ) {
      peek.suppress();
      dispatch("close");
    }
  };
  const latest = useRef({
    handleKeyDown,
    toggleDocked,
    peek,
    foldTo,
    dispatch,
  });
  latest.current = { handleKeyDown, toggleDocked, peek, foldTo, dispatch };
  useEffect(() => {
    if (!isEligible) return;
    const onKeyDown = (event: KeyboardEvent) =>
      latest.current.handleKeyDown(event);
    const onPointerDown = () => {
      linkFollowedAt.current = 0;
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isEligible]);

  // Stable, so a fold's own renders here leave the memoized sidebar, and the nav inside it, alone.
  const sidebarCallbacks = useMemo(
    () => ({
      toggleDocked: (source: "toggle" | "shortcut", isKeyboard: boolean) =>
        latest.current.toggleDocked(source, isKeyboard),
      onCollapse: (source: "edge" | "resize", isKeyboard: boolean) =>
        latest.current.foldTo(false, source, isKeyboard),
      onFollowLink: () => {
        linkFollowedAt.current = Date.now();
      },
      onFocusLeave: () => latest.current.dispatch("leave"),
    }),
    [],
  );
  const sidebarValue = useMemo(
    () => ({
      overlay: overlay === "closed" ? null : overlay,
      toggleDocked: sidebarCallbacks.toggleDocked,
    }),
    [overlay, sidebarCallbacks],
  );
  const expand = useCallback(
    (isKeyboard: boolean) => latest.current.toggleDocked("toggle", isKeyboard),
    [],
  );
  // Hovering the toggle peeks, as the gutter does; a phone's toggle only opens the sheet.
  const canPeek = isActive && nav.canNavOpen;
  const peekTrigger = useMemo<MiniNavTriggerValue["peekTrigger"]>(
    () =>
      canPeek
        ? {
            onPointerEnter: (event) =>
              latest.current.peek.onPointerEnter(event),
            onPointerMove: (event) => latest.current.peek.onPointerMove(event),
            onPointerLeave: (event) =>
              latest.current.peek.onPointerLeave(event),
            onPointerDown: () => latest.current.peek.onPointerDown(),
          }
        : undefined,
    [canPeek],
  );
  const value = useMemo<MiniNavContextValue>(
    () => ({
      hasCrumbBars,
      registerCrumbBar,
      registerLoadingState,
      expand,
      linkFollowedAt,
      Trail: NavPlaceTrail,
    }),
    [hasCrumbBars, registerCrumbBar, registerLoadingState, expand],
  );
  const isDocking = fold === "expanding" || fold === "dockingPeek";
  const triggerValue = useMemo<MiniNavTriggerValue>(
    () => ({ hasTrigger: isActive, isDocking, peekTrigger }),
    [isActive, isDocking, peekTrigger],
  );

  return (
    <MiniNavContext.Provider value={value}>
      <MiniNavTriggerContext.Provider value={triggerValue}>
        <GeneratedCrumbBarContext.Provider
          value={isActive && fold !== "expanding" && fold !== "dockingPeek"}
        >
          <MiniNavPeekContext.Provider value={overlay === "peek"}>
            <CrumbBarEndSlotContext.Provider value={crumbBarEndSlot}>
              <MiniNavSidebarContext.Provider value={sidebarValue}>
                {isEligible && nav.canNavOpen && (
                  <SidebarFrame
                    frameRef={frameRef}
                    isPeeking={overlay === "peek"}
                    peekTop={peekTop}
                    fold={fold}
                    isHeld={isHeld}
                    onCollapse={sidebarCallbacks.onCollapse}
                    onFollowLink={sidebarCallbacks.onFollowLink}
                    onFocusLeave={sidebarCallbacks.onFocusLeave}
                  />
                )}
                {isEligible && !nav.canNavOpen && (
                  <SidebarSheet
                    isOpen={overlay === "sheet"}
                    isFromKeyboard={isSheetFromKeyboard}
                    onClose={() => dispatch("close")}
                  />
                )}
              </MiniNavSidebarContext.Provider>
              {children}
              {/* A page with no card, and so no crumb bar, gets the toggle floating where the bar would have held it. */}
              {isActive && crumbBarCount === 0 && loadingStateCount === 0 && (
                <Box
                  data-nav-toggle-row
                  position="fixed"
                  // Where the crumb bar's toggle sits on a page with a card: the card's inset, then the bar's padding.
                  top={5}
                  left={8}
                  display="flex"
                  zIndex="sticky"
                >
                  <MiniNavTrigger />
                </Box>
              )}
              {/* The gutter left of the page card, full height, which peeks the sidebar as its toggle does. */}
              {canPeek && (
                <Box
                  ref={gutterRef}
                  data-nav-peek-gutter
                  aria-hidden
                  position="fixed"
                  left={0}
                  top={0}
                  bottom={0}
                  width={PEEK_GUTTER_WIDTH}
                  zIndex="sticky"
                  {...peekTrigger}
                />
              )}
            </CrumbBarEndSlotContext.Provider>
          </MiniNavPeekContext.Provider>
        </GeneratedCrumbBarContext.Provider>
      </MiniNavTriggerContext.Provider>
    </MiniNavContext.Provider>
  );
};

/**
 * The real docked sidebar, kept mounted so its drill state survives a collapse.
 * Docked, it sits flush and resizable beside the page; collapsed, it peeks in over the page as a panel inset like the page card.
 */
const SidebarFrame = memo(function SidebarFrame({
  frameRef,
  isPeeking,
  peekTop,
  fold,
  isHeld,
  onCollapse,
  onFollowLink,
  onFocusLeave,
}: {
  frameRef: RefObject<HTMLDivElement>;
  isPeeking: boolean;
  /** Where the peeked sidebar's top rests: below its toggle's row. */
  peekTop: number;
  fold: Fold | null;
  isHeld: { current: boolean };
  onCollapse: (source: "edge" | "resize", isKeyboard: boolean) => void;
  onFollowLink: () => void;
  /** The keyboard's focus that held a peek open left it, with no pointer on it to keep it. */
  onFocusLeave: () => void;
}) {
  const nav = useNavWidth();
  const peekInset = useSpacePx(PEEK_INSET);
  const pageInset = useSpacePx(MINI_NAV_PAGE_INSET);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDocked = nav.isNavOpen;
  const wasDocked = useRef(isDocked);
  useLayoutEffect(() => {
    // Commit the hidden geometry before another render can re-enable the peek transition.
    if (wasDocked.current && !isDocked) {
      frameRef.current?.getBoundingClientRect();
    }
    wasDocked.current = isDocked;
  }, [isDocked, frameRef]);
  const isFloating = !isDocked && !fold;
  const hasPanelChrome = isFloating || fold === "dockingPeek";
  const isShown =
    isDocked || isPeeking || (fold !== null && fold !== "collapsing");
  // Far enough left that the floating panel and its shadow clear the window, whatever its width.
  const hiddenX = -(nav.dockedWidth + 2 * peekInset);
  const isHeldByKeyboard = useRef(false);

  return (
    <Box
      ref={frameRef}
      position={isDocked ? "sticky" : "fixed"}
      top={isFloating ? `${peekTop}px` : 0}
      bottom={isFloating ? PEEK_INSET : undefined}
      left={isFloating ? PEEK_INSET : 0}
      height={isFloating ? "auto" : "100vh"}
      width={`${nav.dockedWidth}px`}
      flexShrink={0}
      // Docked, above the page it sits beside, as the rail was. Peeked, above the page's sticky bars, which come later
      // in the DOM, and under overlays, modals, and the lists it opens itself.
      zIndex={isDocked ? 2 : "banner"}
      bg={isDocked ? undefined : "base.background"}
      overflow={hasPanelChrome ? "hidden" : undefined}
      borderRadius={hasPanelChrome ? "2xl" : undefined}
      // Kept docked too, clear, so the nav inside is as wide in every state and nothing shifts or re-truncates as a peek docks.
      borderWidth="hairline"
      borderStyle="solid"
      borderColor={hasPanelChrome ? "base.border" : "transparent"}
      boxShadow={hasPanelChrome ? "lg" : undefined}
      {...(isShown ? {} : { inert: "" })}
      // A pure function of the state, so no slide can leave the docked sidebar offset: docked, it has no transform.
      transform={isShown ? undefined : `translateX(${hiddenX}px)`}
      visibility={isShown || fold ? "visible" : "hidden"}
      // A completed resize must stay hidden when it becomes a floating panel; only a later peek gets another slide.
      transitionProperty={
        isDocked || fold || wasDocked.current ? "none" : "transform, visibility"
      }
      transitionDuration={isShown ? "normal" : "fast"}
      transitionTimingFunction="easeOutCubic"
      _mediaReduceMotion={{ transitionProperty: "none" }}
      // Only a keyboard's click, which has no pointer position, hands focus on to the next page's trigger.
      onClickCapture={(event) => {
        if (
          event.detail === 0 &&
          (event.target as HTMLElement).closest("a[href]")
        ) {
          onFollowLink();
        }
      }}
      // Keyboard focus in the peek, or focus in a list it opened outside its box, holds it; React's focus events cross portals.
      // A click's focus doesn't hold it, so the pointer leaving still closes it.
      onFocusCapture={(event) => {
        const target = event.target as HTMLElement;
        const isInside = Boolean(frameRef.current?.contains(target));
        isHeldByKeyboard.current = isInside && target.matches(":focus-visible");
        isHeld.current = !isInside || isHeldByKeyboard.current;
      }}
      onBlurCapture={() => {
        const wasHeldByKeyboard = isHeldByKeyboard.current;
        isHeldByKeyboard.current = false;
        isHeld.current = false;
        // Only the keyboard's own focus closes the peek as it leaves; a pointer's peek keeps its grace period.
        if (!wasHeldByKeyboard) return;
        // A list the sidebar opened takes focus through a portal, which the capture above holds again before this runs.
        window.setTimeout(() => {
          const frame = frameRef.current;
          if (
            !isHeld.current &&
            frame &&
            !frame.contains(document.activeElement) &&
            !frame.matches(":hover")
          ) {
            onFocusLeave();
          }
        });
      }}
    >
      <Box height="100%" overflow="hidden">
        <Box ref={contentRef} height="100%">
          <ExpandedNav drillsInPlace={!isDocked} />
        </Box>
      </Box>
      {isDocked && !fold && (
        <SidebarResizeHandle
          frameRef={frameRef}
          contentRef={contentRef}
          collapsedWidth={pageInset}
          width={nav.dockedWidth}
          onResize={nav.setDockedWidth}
          onCollapse={onCollapse}
        />
      )}
    </Box>
  );
});

/**
 * Where the sidebar can't dock, as on a phone, the toggle opens it as a sheet over a scrim, with focus kept inside.
 * Focus starts on the sheet itself, so a tap draws no ring; a keyboard's open moves on to the current page's row.
 */
const SidebarSheet = ({
  isOpen,
  isFromKeyboard,
  onClose,
}: {
  isOpen: boolean;
  isFromKeyboard: { current: boolean };
  onClose: () => void;
}) => {
  const { dockedWidth } = useNavWidth();
  const sheetRef = useRef<HTMLDivElement>(null);
  // The drawer's scroll lock stops a wheel or a swipe, not PageDown or the arrow keys, so the window stops scrolling too.
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    root.style.setProperty("overflow-y", "hidden");
    return () => {
      root.style.removeProperty("overflow-y");
    };
  }, [isOpen]);
  return (
    <Drawer
      isOpen={isOpen}
      placement="left"
      size="xs"
      showOverlay
      trapFocus
      initialFocusRef={sheetRef}
      onClose={onClose}
      // The scrim: htui's drawer overlay has no color of its own, so the container behind the sheet takes the modal's.
      containerStyles={{ bg: "blackAlpha.40" }}
      contentWrapperStyles={{
        "aria-label": "Navigation",
        mt: 0,
        mr: 0,
        maxWidth: `${dockedWidth}px`,
        borderTopRadius: "none",
        bg: "base.background",
        boxShadow: "lg",
      }}
    >
      <Box
        ref={sheetRef}
        {...{ [MINI_NAV_SHEET_ATTRIBUTE]: "" }}
        tabIndex={-1}
        height="100%"
        _focus={{ outline: "none" }}
        onFocus={(event) => {
          if (event.target !== event.currentTarget || !isFromKeyboard.current) {
            return;
          }
          const sheet = event.currentTarget;
          // Once: a later press on the sheet's blank space focuses it again, and must not move to the row.
          isFromKeyboard.current = false;
          // The current page's row, else the list's first; a selector list would match whichever row comes first.
          (
            sheet.querySelector<HTMLElement>("[aria-current='page']") ??
            sheet.querySelector<HTMLElement>(`${NAV_LIST_SELECTOR} a`)
          )?.focus();
        }}
      >
        <NavRowHeightContext.Provider value={10}>
          <ExpandedNav drillsInPlace />
        </NavRowHeightContext.Provider>
      </Box>
    </Drawer>
  );
};

/**
 * The docked nav, expanded at the docked width wherever it shows; in an overlay, a section's row drills in place.
 * Memoized, with a value that docking or collapsing leaves unchanged, so a fold never re-renders the nav inside it.
 */
const ExpandedNav = memo(function ExpandedNav({
  drillsInPlace,
}: {
  drillsInPlace: boolean;
}) {
  const {
    canNavOpen,
    setIsNavOpen,
    setIsNavTemporarilyOpen,
    isNavHidden,
    setIsNavHidden,
    isMiniNavEligible,
    setIsMiniNavEligible,
    dockedWidth,
    setDockedWidth,
  } = useNavWidth();
  const expandedNav = useMemo<NavWidthContextType>(() => {
    const width = `${dockedWidth}px`;
    return {
      isNavOpen: true,
      // Only the layout reads it, and the nav inside the frame is never the collapsed one.
      isMiniNav: false,
      navWidth: { base: width, lg: width },
      canNavOpen,
      setIsNavOpen,
      setIsNavTemporarilyOpen,
      isNavHidden,
      setIsNavHidden,
      isMiniNavEligible,
      setIsMiniNavEligible,
      dockedWidth,
      setDockedWidth,
    };
  }, [
    canNavOpen,
    setIsNavOpen,
    setIsNavTemporarilyOpen,
    isNavHidden,
    setIsNavHidden,
    isMiniNavEligible,
    setIsMiniNavEligible,
    dockedWidth,
    setDockedWidth,
  ]);
  return (
    <NavWidthContext.Provider value={expandedNav}>
      <NavDrillsInPlaceContext.Provider value={drillsInPlace}>
        <Nav isFramed />
      </NavDrillsInPlaceContext.Provider>
    </NavWidthContext.Provider>
  );
});

/**
 * A page's trail under the nav section it sits in, docked or collapsed alike: "Projects › Tasks › …".
 * A followed crumb hands its focus on to the next page's trigger.
 */
const NavPlaceTrail = ({ crumbs, renderLead }: CrumbTrailProps) => {
  const context = useContext(MiniNavContext);
  // Breadcrumbs' own click handlers take no event, so the press's modality is read on its way down.
  const isKeyboardClick = useRef(false);
  const place = useNavPlace();
  // The section's nav icon leads the bar in the toggle's slot, so no crumb carries one.
  const trail = placeTrail(crumbs, place).map((crumb) => ({
    ...crumb,
    icon: undefined,
  }));
  const lead = renderLead(place.section?.icon);
  if (trail.length === 0) return lead;
  return (
    <>
      {lead}
      <Box
        flex={1}
        minWidth={0}
        onClickCapture={(event) => {
          isKeyboardClick.current = event.detail === 0;
        }}
      >
        <Breadcrumbs
          crumbs={trail.map(({ link: href, ...crumb }, index) =>
            // The trail ends on the current page, unlinked, even when that page is a section's first.
            href && index < trail.length - 1
              ? {
                  ...crumb,
                  link: href,
                  onClick: () => {
                    analytics.track("nav_breadcrumb_clicked", { depth: index });
                    crumb.onClick?.();
                    // Only a keyboard's follow moves focus on, so a pointer's never opens the trigger's tooltip.
                    if (!isKeyboardClick.current || !context) return;
                    context.linkFollowedAt.current = Date.now();
                    // A page that keeps its trigger mounted keeps focus on it too.
                    focusNavTarget(MINI_NAV_TRIGGER_SELECTOR, {
                      onlyIfLost: true,
                    });
                  },
                }
              : crumb,
          )}
        />
      </Box>
    </>
  );
};
